import { useContext } from 'react';
import { Button, Col, Row, Table, Tag } from 'antd';
import {
    BarChartOutlined,
    HeartOutlined,
    PlusOutlined,
    ShopOutlined,
    TeamOutlined,
    UserDeleteOutlined,
} from '@ant-design/icons';

import useSocialInstitutions from './useSocialInstitution';
import PageHeader from '../../components/PageHeader';
import KpiCard from '../../components/KpiCard';
import TableCard from '../../components/TableCard';
import TableDicaHint from '../../components/TableDicaHint';
import TablePagination from '../../components/TablePagination';
import SimpleModal from '../../components/SimpleModal';
import ModalAppUsers from './components/ModalAppUsers';
import ModalSupportDistribution from './components/ModalSupportDistribution';
import ModalSocialInstitutionForm from './components/ModalSocialInstitutionForm';
import ModalSocialInstitutionActions from './components/ModalSocialInstitutionActions';
import { ThemeContext } from '../../contexts/ThemeContext';
import { poeValorComPontosEmMil } from '../../utils/Conversores';

export default function SocialInstitutions() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        isLoading,
        isDeleteModalVisible,
        isLoadingDelete,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleCloseFormModal,
        handleSuccessForm,
        modalFormVisible,
        socialInstitutionToEdit,
        modalActionsVisible,
        socialInstitutionForActions,
        handleOpenRowActions,
        handleCloseRowActions,
        handleDeleteSocialInstitution,
        handleConfirmDeleteSocialInstitution,
        handleCancelDeleteSocialInstitution,
        columns,
        tableData,
        socialInstitutionBeingDeleted,
        handleShowModalUsers,
        modalUsersVisible,
        socialInstitutionSelected,
        stats,
        pagination,
        handlePaginationChange,
        modalDistributionVisible,
        setModalDistributionVisible,
    } = useSocialInstitutions();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Header */}
            <PageHeader
                icon={<ShopOutlined />}
                title="Instituições Sociais"
                tag={<Tag color="blue">{poeValorComPontosEmMil(tableData.length)} cadastradas</Tag>}
                subtitle="Gerencie as entidades beneficentes e acompanhe a distribuição de apoio dos cidadãos do município"
                actions={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleOpenCreateModal}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontWeight: 600,
                            height: 38,
                            borderRadius: 8,
                        }}
                    >
                        Nova Instituição
                    </Button>
                }
            />

            {/* KPI Cards */}
            <Row gutter={[12, 12]}>
                <Col xs={24} sm={12} lg={6}>
                    <KpiCard
                        icon={<TeamOutlined />}
                        title="Total de Usuários"
                        value={poeValorComPontosEmMil(stats.totalUsers || 0)}
                        accentColor="#1677ff"
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <KpiCard
                        icon={<HeartOutlined />}
                        title="Apoiadores"
                        value={poeValorComPontosEmMil(stats.totalLinkedUsers)}
                        accentColor="#eb2f96"
                        extra={
                            <Button
                                size="small"
                                type="primary"
                                ghost
                                icon={<BarChartOutlined style={{ fontSize: 12, color: "#eb2f96" }} />}
                                onClick={() => setModalDistributionVisible(true)}
                                style={{
                                    borderRadius: 4,
                                    borderColor: "#eb2f96",
                                    color: "#eb2f96"
                                }}
                            />
                        }
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <KpiCard
                        icon={<UserDeleteOutlined />}
                        title="Sem Vínculo"
                        value={poeValorComPontosEmMil(stats.totalUnlinkedUsers)}
                        accentColor="#fa8c16"
                    />
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <KpiCard
                        icon={<ShopOutlined />}
                        title="Entidades Cadastradas"
                        value={poeValorComPontosEmMil(stats.totalEntities)}
                        accentColor="#52c41a"
                    />
                </Col>
            </Row>

            <TableDicaHint text="Dê um clique na linha para abrir as opções da instituição (ver apoiadores, editar ou excluir) ou utilize os botões rápidos de ação à direita." />

            <TablePagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={tableData.length}
                itemName="instituições"
                pageSizeOptions={['10', '25', '50', '100']}
                onChange={handlePaginationChange}
            />

            {/* Main Table */}
            <TableCard>
                <Table
                    rowKey="id"
                    className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
                    columns={columns as any}
                    dataSource={tableData.slice(
                        (pagination.current - 1) * pagination.pageSize,
                        pagination.current * pagination.pageSize
                    )}
                    loading={isLoading}
                    pagination={false}
                    scroll={{ x: 950 }}
                    onRow={(record) => ({
                        onClick: () => handleOpenRowActions(record),
                        style: {
                            cursor: 'pointer',
                        },
                    })}
                />
            </TableCard>

            <TablePagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={tableData.length}
                itemName="instituições"
                pageSizeOptions={['10', '25', '50', '100']}
                onChange={handlePaginationChange}
            />

            {/* Modal: Delete Confirmation */}
            <SimpleModal
                open={isDeleteModalVisible}
                onOk={handleConfirmDeleteSocialInstitution}
                okButtonProps={{
                    disabled: isLoadingDelete,
                    loading: isLoadingDelete,
                }}
                cancelButtonProps={{
                    disabled: isLoadingDelete,
                }}
                okText="Deletar"
                onCancel={handleCancelDeleteSocialInstitution}
                title={`Deseja realmente deletar a instituição: "${socialInstitutionBeingDeleted?.name}"?`}
            />

            {/* Modal: Linked Users */}
            {socialInstitutionSelected && (
                <ModalAppUsers
                    onClose={() => handleShowModalUsers()}
                    open={modalUsersVisible}
                    socialInstitutionSelected={socialInstitutionSelected}
                />
            )}

            {/* Modal: Support Distribution Stats */}
            <ModalSupportDistribution
                open={modalDistributionVisible}
                onClose={() => setModalDistributionVisible(false)}
                institutions={tableData}
                totalLinkedUsers={stats.totalLinkedUsers}
                totalUsers={stats.totalUsers}
                onSelectInstitution={(inst) => handleShowModalUsers(inst)}
            />

            {/* Modal: Create / Edit Social Institution */}
            <ModalSocialInstitutionForm
                open={modalFormVisible}
                onClose={handleCloseFormModal}
                socialInstitution={socialInstitutionToEdit}
                onSuccess={handleSuccessForm}
            />

            {/* Modal: Row Click Options (View Users, Edit, Delete) */}
            <ModalSocialInstitutionActions
                open={modalActionsVisible}
                onClose={handleCloseRowActions}
                socialInstitution={socialInstitutionForActions}
                onViewUsers={(inst) => handleShowModalUsers(inst)}
                onEdit={(inst) => handleOpenEditModal(inst)}
                onDelete={(inst) => handleDeleteSocialInstitution(inst)}
            />
        </div>
    );
}

